import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { noop } from '../../../utils/noop';
import { withProduct } from '../../../utils/ProductContext';
import { ServicesPrices } from '../../Prices/ServicesPrices';
import { Warning } from '../../Warning/Warning';
import { getWarrantyBenefits, getWarrantyLabel, isWarrantyRecommended } from './WarrantySelect.helpers';
import { WarrantySelectEnhancedStyles } from './WarrantySelectEnhanced.style';

const WarrantySelectEnhanced = ({ errorMessage, handleWarrantyOptionsSelected, options, appCtx, product }) => {
  const [benefitsVisibility, setBenefitsVisibility] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const initialSelectionDone = useRef(false);
  const toggleBenefitsVisibility = () => {
    setBenefitsVisibility((prevState) => !prevState);
  };

  const { deviceType, siteConfig, regionCode } = appCtx;
  const isMobile = deviceType === 'mobile';
  const gCategoryId = product?.GSCCategoryId || '';
  const sellerName = (product?.sellerName || '').toLowerCase();
  const {
    WARRANTIES_MODAL_LABEL = 'Extiende la garantía del producto',
    WARRANTY_BENEFITS_LABEL = 'Beneficios de extender la garantía',
    PDP_NO_THANKS = 'Sin garantía extendida',
    WARRANTY_LABEL_TEMPLATE = '{warrantyName}',
    RECOMMENDED_WARRANTY = 'Recomendado',
    MORE_WARRANTY = 'Más beneficios',
  } = siteConfig?.textDictionary || {};
  const moreWarrantyBenefitsLink = siteConfig?.endpoints?.moreWarrantyBenefitsLink;
  const warranties = options.filter((option) => option.offeringId || option.value);

  const warrantyBenefits = getWarrantyBenefits({
    siteConfig,
    regionCode,
    gCategoryId,
  });

  const handleOptionChange = (index) => {
    if (selectedIndex !== index) {
      handleWarrantyOptionsSelected(options[index]);
      setSelectedIndex(index);
    }
  };

  useEffect(() => {
    if (options && options.length > 0 && !initialSelectionDone.current) {
      const warrantyOptions = options.filter((option) => option.offeringId || option.value);
      if (warrantyOptions.length === 0) return;
      initialSelectionDone.current = true;
      if (warrantyOptions.length > 1) {
        const middleIndex = Math.floor((warrantyOptions.length - 1) / 2);
        setSelectedIndex(middleIndex);
        handleWarrantyOptionsSelected(warrantyOptions[middleIndex]);
        return;
      }
      setSelectedIndex(0);
      handleWarrantyOptionsSelected(warrantyOptions[0]);
    }
  }, [options]);

  const renderWarrantySku = (warranty, index) => {
    const { textLabel, textPrice, prices, discountBadge } = warranty;
    const warrantyLabel = getWarrantyLabel({
      textLabel,
      sellerName,
      warrantyLabelTemplate: WARRANTY_LABEL_TEMPLATE,
    });
    const isSelected = index === selectedIndex;
    const isRecommended = isWarrantyRecommended(warrantyLabel);
    return (
      <>
        <button
          type="submit"
          id="warranty-sku"
          className={`warranty-sku ${isSelected ? 'selected' : ''}`}
          onClick={() => handleOptionChange(index)}
        >
          <div className="label-wrapper">
            <span className={`radio-button ${isSelected ? 'full' : 'empty'}`} />
            <div className="warranty-details">
              <span className="warranty-text">
                <p>{warrantyLabel}</p>
                {isRecommended && <p className="recommended-badge">{RECOMMENDED_WARRANTY}</p>}
              </span>
            </div>
          </div>
          {prices ? (
            <ServicesPrices priceList={prices} discountBadge={discountBadge} />
          ) : (
            <span className="price">{textPrice}</span>
          )}
        </button>
        <style jsx>{WarrantySelectEnhancedStyles}</style>
      </>
    );
  };

  return (
    <div className="warranty-wrapper">
      <div className="title">
        <div className="warranty-icon" />
        <p>{WARRANTIES_MODAL_LABEL}</p>
      </div>
      <div className="body">
        <div className="sku-list">
          {warranties.map((warranty, index) => renderWarrantySku(warranty, index))}
          <button
            type="submit"
            className={`warranty-sku ${warranties.length === selectedIndex ? 'selected' : ''}`}
            onClick={() => handleOptionChange(warranties.length)}
          >
            <div className="label-wrapper">
              <span className={`radio-button ${selectedIndex === warranties.length ? 'full' : 'empty'}`} />
              <span className="warranty-details">
                <span className="warranty-text">{PDP_NO_THANKS}</span>
              </span>
            </div>
          </button>
        </div>
        {warrantyBenefits.length > 0 && (
          <div className="benefits-list">
            <div className="title">
              <p>{WARRANTY_BENEFITS_LABEL}</p>
              {isMobile && (
                <button type="submit" onClick={toggleBenefitsVisibility}>
                  <i className={`${benefitsVisibility ? 'collapse' : 'expand'}`} />
                </button>
              )}
            </div>
            {(!isMobile || benefitsVisibility) && (
              <div className="list">
                {warrantyBenefits.map((benefit) => (
                  <span className="benefit">
                    <i className="tick-icon" />
                    <p>{benefit}</p>
                  </span>
                ))}
                {moreWarrantyBenefitsLink && (
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={moreWarrantyBenefitsLink}
                    className="link-wrapper"
                    tabIndex={0}
                  >
                    <span className="more-benefits">{MORE_WARRANTY}</span>
                    <i className="csicon-right" />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {errorMessage && <Warning label={errorMessage} iconSize="copy2" labelSize="copy5" />}
      <style jsx>{WarrantySelectEnhancedStyles}</style>
    </div>
  );
};

WarrantySelectEnhanced.defaultProps = {
  errorMessage: null,
  handleWarrantyOptionsSelected: noop,
  options: [],
  appCtx: {},
  product: {},
};

WarrantySelectEnhanced.propTypes = {
  errorMessage: PropTypes.string,
  handleWarrantyOptionsSelected: PropTypes.func,
  options: PropTypes.array,
  appCtx: PropTypes.object,
  product: PropTypes.object,
};

export default withProduct(WarrantySelectEnhanced);
